import { Form, Tooltip } from 'antd';
import imgcvv from '../../../../assets/cvv.png';
import Input from '../../../Input';

import {
  QuestionCircleFilled
} from '@ant-design/icons';
import { FormInstance } from 'antd/lib';
import { ICardBrandRule } from '../../../../interfaces/ICheckout';
import { useForm } from '../../context';
import { cardBrandLabel, checkLuhn, creditCardMask, dateMask, findBlockedCardBrand } from '../../helper';
import Installments from './Installments';

interface IProps {
  name?: (string | number)[];
  form?: FormInstance;
  disableInstallments?: boolean;
  basePath?: string;
  // A página de assinatura renderiza este componente fora do FormProvider do checkout, então
  // precisa entregar a lista por prop.
  cardBrandRules?: ICardBrandRule[];
}

const CreditCard = ({ name, form, disableInstallments, basePath, cardBrandRules }: IProps) => {
  name = name ?? [];
  basePath = basePath ?? 'payments';

  const formContext = useForm();
  form = form ?? formContext.form;
  const brandRules = cardBrandRules ?? formContext.checkout?.cardBrandRules;

  return (
    <>
      <Form.Item name={[...name, 'id']} hidden>
        <Input hidden />
      </Form.Item>

      <div className='grid grid-cols-1 sm:grid-cols-2 sm:gap-4'>
        <Form.Item
          label="Número do cartão"
          name={[...name, 'number']}
          rules={[
            {
              required: true,
              validator: (_, value) => {
                const fixed = creditCardMask.clean(value);
                if (!fixed) {
                  return Promise.reject('Por favor, digite o número do cartão!');
                }

                if (fixed.length < 13 || !checkLuhn(fixed)) {
                  return Promise.reject('Por favor, digite um número de cartão válido!');
                }

                const blocked = findBlockedCardBrand(fixed, brandRules);
                if (blocked) {
                  return Promise.reject(`Não aceitamos cartões ${cardBrandLabel(blocked)}. Use outro cartão.`);
                }

                return Promise.resolve();
              },
            },
          ]}
        >
          <Input
            onChange={(e) => {
              const fixed = creditCardMask.apply(e.target.value);
              form?.setFieldValue([basePath, ...(name as string[]), 'number'], fixed);

              form?.validateFields([basePath, ...(name as string[]), 'number'], {
                recursive: true,
                dirty: true,
              });
            }}
            placeholder="Digite o número do cartão"
          />
        </Form.Item>

        <Form.Item
          label="Nome impresso no cartão"
          name={[...name, 'holder']}
          rules={[
            {
              required: true,
              message: 'Por favor, digite o nome impresso no cartão!',
            },
          ]}
        >
          <Input
            placeholder="Digite o nome impresso no cartão"
          />
        </Form.Item>
      </div>

      <div className='grid grid-cols-2 gap-4'>
        <Form.Item
          label="Validade"
          name={[...name, 'expiration']}
          rules={[
            {
              required: true,
              validator: (_, value) => {
                const fixed = dateMask.clean(value);
                if (!fixed) {
                  return Promise.reject('Por favor, digite a validade do cartão!');
                }

                const currentDay = new Date().getDate();
                const [month, year] = [fixed.slice(0, 2), fixed.slice(2, 4)].map(Number);
                const expiration = new Date(
                  `20${year}-${month.toString().padStart(2, '0')}-${currentDay.toString().padStart(2, '0')}`
                );

                if (!(expiration instanceof Date && !isNaN(expiration.getTime())) || expiration < new Date()) {
                  return Promise.reject('Por favor, digite uma data válida!');
                }

                return Promise.resolve();
              },
            },
          ]}
        >
          <Input
            onChange={(e) => {
              const fixed = dateMask.apply(e.target.value);
              form?.setFieldValue([basePath, ...(name as string[]), 'expiration'], fixed);

              form?.validateFields([basePath, ...(name as string[]), 'expiration'], {
                recursive: true,
                dirty: true,
              });
            }}
            placeholder="Digite a validade (MM/AA)"
          />
        </Form.Item>

        <Form.Item
          name={[...name, 'cvv']}
          label={
            <Tooltip
              title={<img className='w-50' src={imgcvv} />}
            >
              <span className="mr-2">CVV</span>
              <QuestionCircleFilled style={{ color: '#000' }} />
            </Tooltip>
          }
          rules={[
            {
              required: true,
              message: 'Por favor, digite o CVV do cartão!',
            },
            {
              min: 3,
              message: 'Por favor, digite um CVV válido!',
            },
            {
              max: 4,
              message: 'Por favor, digite um CVV válido!',
            },
          ]}
        >
          <Input maxLength={4} placeholder="Digite o CVV" />
        </Form.Item>
      </div>

      {!disableInstallments && <Installments name={name} />}
    </>
  );
};

export default CreditCard;
